import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Receipt,
  FileText,
  ScrollText,
  Coins,
  Plus,
  Download,
  Trash2,
  Pencil,
  Save,
  XCircle,
  Upload,
  ExternalLink,
  Search,
  X,
  AlertCircle,
  CheckCircle2,
  CreditCard,
  Loader2,
} from "lucide-react";
import api from "../../lib/api";
import { uploadUrl } from "../../lib/uploads";
import {
  salesDocPresign,
  salesDocPutToR2,
  downloadSalesDocToDevice,
} from "../../lib/salesDocsR2";
import FoldBlock from "./FoldBlock";
import OcrProcessingStrip from "./OcrProcessingStrip";
import PortalDocUploader from "../portal/PortalDocUploader";
import { useBranding } from "../../context/TenantBrandingContext";
import Modal from "../Modal";
import ConfirmModal from "../ConfirmModal";
import DatePickerInput from "../DatePickerInput";
import BlurTextarea from "../BlurTextarea";

const CATEGORIES = [
  { key: "quote", label: "Quotes", icon: FileText, accent: "#A78BFA" },
  { key: "sales_order", label: "Sales orders", icon: ScrollText, accent: "#1FB6C1" },
  { key: "invoice", label: "Invoices", icon: Receipt, accent: "#F59E0B" },
  { key: "statement", label: "Statements", icon: Coins, accent: "#10B981" },
  { key: "credit_note", label: "Credit notes", icon: Coins, accent: "#F472B6" },
  { key: "purchase_order", label: "Purchase orders", icon: FileText, accent: "#7C3AED" },
  { key: "payment_proof", label: "Proof of payments", icon: CheckCircle2, accent: "#10B981" },
];
export { CATEGORIES };

/** Per-category lifecycle status whitelist (kept in sync with backend). */
const CATEGORY_STATUSES = {
  quote: ["open", "accepted", "rejected", "expired"],
  sales_order: ["open", "in_progress", "closed", "cancelled"],
  invoice: ["outstanding", "paid", "overdue", "cancelled"],
  statement: ["open", "closed"],
  credit_note: ["open", "applied", "cancelled"],
  purchase_order: ["open", "accepted", "closed", "cancelled"],
  payment_proof: ["open", "verified", "rejected"],
};

/** Tailwind tint colours for status pills. */
const STATUS_PILL = {
  open: "bg-sky-500/15 text-sky-200 border-sky-400/40",
  accepted: "bg-emerald-500/15 text-emerald-200 border-emerald-400/40",
  rejected: "bg-rose-500/15 text-rose-200 border-rose-400/40",
  expired: "bg-stone-500/15 text-stone-200 border-stone-400/40",
  in_progress: "bg-amber-500/15 text-amber-200 border-amber-400/40",
  closed: "bg-stone-500/15 text-stone-200 border-stone-400/40",
  cancelled: "bg-stone-500/15 text-stone-300 border-stone-400/40 line-through",
  outstanding: "bg-amber-500/15 text-amber-200 border-amber-400/40",
  overdue: "bg-rose-500/15 text-rose-200 border-rose-400/40 font-bold",
  paid: "bg-emerald-500/15 text-emerald-200 border-emerald-400/40",
  applied: "bg-emerald-500/15 text-emerald-200 border-emerald-400/40",
  verified: "bg-emerald-500/15 text-emerald-200 border-emerald-400/40",
};

/**
 * Account block — quotes / sales orders / invoices / statements.
 * Shared between manager-facing CustomerDetail and the customer Portal.
 *
 * Props:
 *   customerId:        the customer this block belongs to
 *   sales:             pre-fetched array (or null → component fetches itself)
 *   canEdit:           manager-edit mode → +upload / edit / delete
 *   canDelete:         override (managers always can; clients never can)
 *   canRequestRemoval: client-side action — opens a "request removal" modal
 *   defaultOpen
 *   onChanged          parent reload callback
 *   onRequestRemoval   (item) => triggers RemovalRequest flow
 */
export default function AccountBlock({
  customerId,
  documents = [],
  canEdit = false,
  canDelete = false,
  canRequestRemoval = false,
  canMarkPaid = false,
  currentUserId = null,
  isPortal = false,
  hideTotals = false,
  defaultOpen = false,
  onChanged,
  onRequestRemoval,
}) {
  const nav = useNavigate();
  // Wave 29 Chunk 8e — Upload/Edit/Pay now happen on full-page routes
  // (DESIGN_CONTRACT Rule 0). Keep only the delete confirmation modal here
  // since it's a single-action confirm (allowed by Rule 0).
  const [payTarget, setPayTarget] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");

  const filteredDocuments = useMemo(() => {
    const list = Array.isArray(documents) ? documents : [];
    const term = search.trim().toLowerCase();
    if (!term) return list;
    return list.filter((d) => {
      const hay = [
        d.title,
        d.document_number,
        d.notes,
        d.category,
        d.file_name,
        d.amount ? String(d.amount) : "",
      ]
        .map((x) => (x ? String(x).toLowerCase() : ""))
        .join(" | ");
      return hay.includes(term);
    });
  }, [documents, search]);

  const grouped = useMemo(() => {
    const g = Object.fromEntries(CATEGORIES.map((c) => [c.key, []]));
    (Array.isArray(filteredDocuments) ? filteredDocuments : []).forEach((d) => {
      if (g[d.category]) g[d.category].push(d);
    });
    Object.values(g).forEach((arr) =>
      arr.sort(
        (a, b) =>
          new Date(b.document_date || b.created_at) - new Date(a.document_date || a.created_at),
      ),
    );
    return g;
  }, [filteredDocuments]);

  const totalAmount = useMemo(() => {
    return (Array.isArray(documents) ? documents : []).reduce((s, d) => s + (Number(d.amount) || 0), 0);
  }, [documents]);

  const del = async () => {
    if (!confirmDel) return;
    setBusy(true);
    try {
      await api.delete(`/sales-documents/${confirmDel.id}`);
      setConfirmDel(null);
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <FoldBlock
      icon={Receipt}
      iconColor="#F59E0B"
      title="Account"
      count={documents.length}
      defaultOpen={defaultOpen}
      testId="block-account"
      emptyHint="No quotes, orders, invoices or statements yet."
      alwaysRenderChildren={canEdit || canRequestRemoval || isPortal}
      headerExtra={
        !hideTotals && documents.length > 0 && totalAmount > 0 ? (
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-emerald-500/40 text-emerald-300 bg-emerald-500/5 font-mono">
            R {totalAmount.toLocaleString('en-GB')}
          </span>
        ) : null
      }
    >
      <div className="space-y-3">
        {/* Iter 234/235 — live OCR progress for both staff and portal docs. */}
        <OcrProcessingStrip
          customerId={customerId}
          initialDocs={documents}
          apiPath={isPortal ? "/sales-documents" : undefined}
          label={isPortal ? "uploads" : "documents"}
        />
        {/* Iter 235 — portal clients can attach their own POs / proof-of-payments. */}
        {isPortal && (
          <PortalDocUploader onUploaded={onChanged} />
        )}
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#647087]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title, ref, amount, notes…"
            className="hv-input pl-8 pr-8 py-1.5 text-xs"
            data-testid="account-search"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[#647087] hover:text-[#dde3ec]"
              title="Clear search"
              data-testid="account-search-clear"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className={isPortal ? "grid grid-cols-1 gap-2" : "grid grid-cols-1 sm:grid-cols-2 gap-2"}>
          {CATEGORIES.map((cat) => (
            <CategoryColumn
              key={cat.key}
              cat={cat}
              items={grouped[cat.key]}
              customerId={customerId}
              canEdit={canEdit}
              canDelete={canDelete}
              canRequestRemoval={canRequestRemoval}
              canMarkPaid={canMarkPaid}
              currentUserId={currentUserId}
              isPortal={isPortal}
              onAdd={() => nav(`/customers/${customerId}/sales-docs/new?category=${cat.key}`)}
              onEdit={(d) => nav(`/sales-documents/${d.id}/edit`)}
              onDelete={(d) => setConfirmDel(d)}
              onRequestRemoval={onRequestRemoval}
              onMarkPayment={(d) => setPayTarget(d)}
              onChanged={onChanged}
            />
          ))}
        </div>
      </div>

      {payTarget && (
        <PaymentModal
          doc={payTarget}
          onClose={() => setPayTarget(null)}
          onSaved={() => {
            setPayTarget(null);
            onChanged?.();
          }}
        />
      )}

      <ConfirmModal
        open={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        onConfirm={del}
        title="Delete document"
        message={
          <>
            Permanently delete <b>{confirmDel?.title}</b>?
          </>
        }
        confirmLabel="Delete document"
        testId="sales-doc-delete-confirm"
        busy={busy}
      />
    </FoldBlock>
  );
}

function CategoryColumn({ cat, items, customerId, canEdit, canDelete, canRequestRemoval, canMarkPaid, currentUserId, isPortal = false, onAdd, onEdit, onDelete, onRequestRemoval, onMarkPayment, onChanged }) {
  const Icon = cat.icon;
  // Iter 290zp.13z21 — Per-category drag/drop upload. Drop a PDF/image
  // on the card → instant R2 upload + sales_documents row + OCR runs
  // server-side. No modal. The DocRow row that appears handles the
  // "Scanning…" polling automatically.
  const [dropping, setDropping] = useState(false);
  const [uploading, setUploading] = useState([]);  // [{id, name, progress, error?}]

  const handleDroppedFiles = async (filesList) => {
    if (!canEdit || !customerId) return;
    const files = Array.from(filesList || []).filter((f) =>
      f && (f.type?.startsWith("image/") || f.type === "application/pdf"
            || /\.(pdf|jpe?g|png|webp|heic|tiff?)$/i.test(f.name || ""))
    );
    if (files.length === 0) return;
    // Quotes + Sales orders need an audience. For drag-drop, default to
    // internal (DNU) so engineering working docs don't leak to the
    // portal; rep can flip via the DNU/CF chip on the doc row after.
    // Iter 425.b — Purchase Orders are customer-side POs; treat them
    // like invoices/credit notes (always customer-facing, no DNU).
    const defaultAudience =
      (cat.key === "invoice" || cat.key === "credit_note" || cat.key === "statement"
        || cat.key === "purchase_order" || cat.key === "payment_proof")
        ? "customer"
        : "internal";
    for (const f of files) {
      const tmpId = `tmp-${Math.random().toString(36).slice(2, 9)}`;
      setUploading((prev) => [...prev, { id: tmpId, name: f.name, progress: 0 }]);
      try {
        const presign = await salesDocPresign({ customerId, file: f });
        await salesDocPutToR2(presign, f, (pct) => {
          setUploading((prev) =>
            prev.map((u) => (u.id === tmpId ? { ...u, progress: pct } : u))
          );
        });
        const body = {
          customer_id: customerId,
          category: cat.key,
          title: (f.name || "Document").replace(/\.[^.]+$/, ""),
          document_number: null,
          document_date: new Date().toISOString().slice(0, 10),
          amount: null,
          currency: "ZAR",
          file_url: null,
          file_name: f.name,
          file_size: f.size,
          file_type: f.type || null,
          notes: "",
          r2_key: presign.key,
          status: null,
          audience: defaultAudience,
          payment_status: cat.key === "invoice" ? "outstanding" : null,
        };
        await api.post("/sales-documents", body);
        setUploading((prev) => prev.filter((u) => u.id !== tmpId));
        onChanged?.();
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || "Upload failed";
        setUploading((prev) =>
          prev.map((u) => (u.id === tmpId ? { ...u, error: String(msg).slice(0, 80) } : u))
        );
        // Auto-clear the error chip after 8s so the card doesn't accumulate
        // ghost rows.
        setTimeout(() => {
          setUploading((prev) => prev.filter((u) => u.id !== tmpId));
        }, 8000);
      }
    }
  };
  // Invoices get split into Outstanding / Historical so outstanding always
  // bubbles to the top with a pay-now accent.
  const outstanding = cat.key === "invoice" ? items.filter((d) => d.payment_status === "outstanding") : [];
  const paid = cat.key === "invoice" ? items.filter((d) => d.payment_status === "paid") : [];
  const otherInvoices = cat.key === "invoice" ? items.filter((d) => !d.payment_status) : [];
  return (
    <div
      className={`border rounded-md overflow-hidden flex flex-col transition-colors ${dropping ? "border-[#1FB6C1] bg-[#1FB6C1]/[0.06]" : "border-white/5"}`}
      data-testid={`account-category-${cat.key}`}
      onDragOver={(e) => {
        if (!canEdit || !customerId) return;
        const types = Array.from(e.dataTransfer?.types || []);
        if (!types.includes("Files")) return;
        e.preventDefault();
        e.stopPropagation();  // beat any ancestor drop handler
        if (!dropping) setDropping(true);
      }}
      onDragLeave={(e) => {
        // Only clear when leaving the OUTER card (not when moving over
        // a nested child). relatedTarget !== this means we left.
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setDropping(false);
      }}
      onDrop={(e) => {
        if (!canEdit || !customerId) return;
        const types = Array.from(e.dataTransfer?.types || []);
        if (!types.includes("Files")) return;
        e.preventDefault();
        e.stopPropagation();
        setDropping(false);
        handleDroppedFiles(e.dataTransfer.files);
      }}
    >
      <div
        className="flex items-center justify-between px-3 py-2 border-b border-white/5"
        style={{ background: `${cat.accent}10` }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="w-3.5 h-3.5" style={{ color: cat.accent }} />
          <span className="text-xs font-bold tracking-tight">{cat.label}</span>
          <span
            className="text-[10px] font-mono px-1.5 py-0.5 rounded border"
            style={{ borderColor: `${cat.accent}40`, color: cat.accent, background: `${cat.accent}0d` }}
          >
            {items.length}
          </span>
          {outstanding.length > 0 && (
            <span
              className="text-[10px] font-mono px-1.5 py-0.5 rounded-full border border-rose-400/50 text-rose-300 bg-rose-500/10 inline-flex items-center gap-1"
              data-testid={`account-outstanding-chip-${cat.key}`}
              title={`${outstanding.length} outstanding`}
            >
              <AlertCircle className="w-2.5 h-2.5" />
              {outstanding.length} due
            </span>
          )}
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={onAdd}
            className="text-[10px] uppercase tracking-wider hover:underline inline-flex items-center gap-0.5"
            style={{ color: cat.accent }}
            data-testid={`account-add-${cat.key}`}
          >
            <Plus className="w-3 h-3" /> Upload
          </button>
        )}
      </div>

      {/* Iter 290zp.13z21 — In-flight uploads + drop-here hint. Rendered
          ABOVE the doc list so the rep sees the new file appear
          immediately after drop. */}
      {(uploading.length > 0 || dropping) && (
        <div className="border-b border-white/5 bg-white/[0.02] px-3 py-1.5 space-y-1" data-testid={`account-uploading-${cat.key}`}>
          {dropping && uploading.length === 0 && (
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#1FB6C1] flex items-center gap-1.5">
              <Upload className="w-3 h-3" />
              Drop to upload as {cat.label.toLowerCase().replace(/s$/, "")}
            </div>
          )}
          {uploading.map((u) => (
            <div
              key={u.id}
              className="flex items-center gap-2 text-[11px]"
              data-testid={`account-upload-row-${u.id}`}
            >
              <Upload className="w-3 h-3 text-[#1FB6C1] shrink-0" />
              <span className="truncate flex-1 min-w-0 text-[#dde3ec]">{u.name}</span>
              {u.error ? (
                <span className="text-rose-300 text-[10px]">{u.error}</span>
              ) : (
                <span className="text-[#1FB6C1] text-[10px] font-mono">
                  {u.progress < 100 ? `${u.progress}%` : "Scanning…"}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {cat.key === "invoice" ? (
        <>
          {outstanding.length > 0 && (
            <div className="border-b border-rose-500/20 bg-rose-500/5" data-testid="account-outstanding-section">
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-rose-300 font-bold flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Outstanding — action required
              </div>
              <ul className="divide-y divide-white/5">
                {outstanding.map((d) => (
                  <DocRow
                    key={d.id}
                    doc={d}
                    accent={cat.accent}
                    outstanding
                    canEdit={canEdit}
                    canDelete={canDelete}
                    canRequestRemoval={canRequestRemoval}
                    canMarkPaid={canMarkPaid}
                    currentUserId={currentUserId}
                    onEdit={() => onEdit(d)}
                    onDelete={() => onDelete(d)}
                    onRequestRemoval={onRequestRemoval}
                    onMarkPayment={() => onMarkPayment(d)}
                  />
                ))}
              </ul>
            </div>
          )}
          <ul className="divide-y divide-white/5">
            {otherInvoices.length === 0 && paid.length === 0 && outstanding.length === 0 ? (
              <li className="px-3 py-2 text-[11px] text-[#647087] italic">No invoices.</li>
            ) : null}
            {otherInvoices.map((d) => (
              <DocRow
                key={d.id}
                doc={d}
                accent={cat.accent}
                canEdit={canEdit}
                canDelete={canDelete}
                canRequestRemoval={canRequestRemoval}
                canMarkPaid={canMarkPaid}
                currentUserId={currentUserId}
                onEdit={() => onEdit(d)}
                onDelete={() => onDelete(d)}
                onRequestRemoval={onRequestRemoval}
                onMarkPayment={() => onMarkPayment(d)}
              />
            ))}
            {paid.map((d) => (
              <DocRow
                key={d.id}
                doc={d}
                accent={cat.accent}
                paid
                canEdit={canEdit}
                canDelete={canDelete}
                canRequestRemoval={canRequestRemoval}
                canMarkPaid={canMarkPaid}
                currentUserId={currentUserId}
                onEdit={() => onEdit(d)}
                onDelete={() => onDelete(d)}
                onRequestRemoval={onRequestRemoval}
                onMarkPayment={() => onMarkPayment(d)}
              />
            ))}
          </ul>
        </>
      ) : (cat.key === "quote" || cat.key === "sales_order") && !isPortal ? (
        // Wave 29 Chunk 8e — engineering categories get a hard visual split
        // between Internal (DNU) and Customer-facing (CF). Empty subsection
        // shows a hint reminding the operator the audience exists.
        // [Iter 290zp.13y] Portal mode (`isPortal === true`) hides the
        // DNU/CF split entirely — customers must never see internal DNU
        // sections or labels. Backend already filters internal docs from
        // portal responses so the items array is CF-only; we just render
        // it as a plain list without the heading split.
        <AudienceSplitList
          items={items}
          cat={cat}
          canEdit={canEdit}
          canDelete={canDelete}
          canRequestRemoval={canRequestRemoval}
          canMarkPaid={canMarkPaid}
          currentUserId={currentUserId}
          onEdit={onEdit}
          onDelete={onDelete}
          onRequestRemoval={onRequestRemoval}
          onMarkPayment={onMarkPayment}
        />
      ) : (
        <ul className="divide-y divide-white/5">
          {items.length === 0 ? (
            <li className="px-3 py-2 text-[11px] text-[#647087] italic">No {cat.label.toLowerCase()}.</li>
          ) : (
            items.map((d) => (
              <DocRow
                key={d.id}
                doc={d}
                canEdit={canEdit}
                canDelete={canDelete}
                canRequestRemoval={canRequestRemoval}
                canMarkPaid={canMarkPaid}
                currentUserId={currentUserId}
                onEdit={() => onEdit(d)}
                onDelete={() => onDelete(d)}
                onRequestRemoval={onRequestRemoval}
                accent={cat.accent}
                onMarkPayment={() => onMarkPayment(d)}
              />
            ))
          )}
        </ul>
      )}
    </div>
  );
}

function AudienceSplitList({
  items, cat,
  canEdit, canDelete, canRequestRemoval, canMarkPaid, currentUserId,
  onEdit, onDelete, onRequestRemoval, onMarkPayment,
}) {
  const internal = items.filter((d) => (d.audience || "customer") === "internal");
  const customer = items.filter((d) => (d.audience || "customer") !== "internal");
  const rowProps = {
    accent: cat.accent,
    canEdit, canDelete, canRequestRemoval, canMarkPaid,
    currentUserId,
    onRequestRemoval,
  };
  return (
    <div data-testid={`account-audience-split-${cat.key}`}>
      <SubHeader
        label="Internal · DNU"
        count={internal.length}
        tone="rose"
        hint="Engineering — never shared with customer"
        testId={`account-section-dnu-${cat.key}`}
      />
      <ul className="divide-y divide-white/5">
        {internal.length === 0 ? (
          <li className="px-3 py-2 text-[10px] text-[#647087] italic">
            No internal {cat.label.toLowerCase()} yet — upload your detailed DNU version to power project budgets + procurement.
          </li>
        ) : internal.map((d) => (
          <DocRow
            key={d.id}
            doc={d}
            {...rowProps}
            onEdit={() => onEdit(d)}
            onDelete={() => onDelete(d)}
            onMarkPayment={() => onMarkPayment(d)}
          />
        ))}
      </ul>
      <SubHeader
        label="Customer · CF"
        count={customer.length}
        tone="emerald"
        hint="Safe to share via portal"
        testId={`account-section-cf-${cat.key}`}
      />
      <ul className="divide-y divide-white/5">
        {customer.length === 0 ? (
          <li className="px-3 py-2 text-[10px] text-[#647087] italic">
            No customer-facing {cat.label.toLowerCase()} yet.
          </li>
        ) : customer.map((d) => (
          <DocRow
            key={d.id}
            doc={d}
            {...rowProps}
            onEdit={() => onEdit(d)}
            onDelete={() => onDelete(d)}
            onMarkPayment={() => onMarkPayment(d)}
          />
        ))}
      </ul>
    </div>
  );
}

function SubHeader({ label, count, tone, hint, testId }) {
  const cls = tone === "rose"
    ? "text-rose-300 bg-rose-500/5 border-rose-500/15"
    : "text-emerald-300 bg-emerald-500/5 border-emerald-500/15";
  return (
    <div className={`px-3 py-1.5 border-b ${cls} flex items-center justify-between`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold inline-flex items-center gap-1.5">
        {label}
        <span className="text-[9px] font-mono px-1 py-0 border border-current rounded">{count}</span>
      </div>
      <span className="text-[9px] text-[#647087] italic">{hint}</span>
    </div>
  );
}

export function DocRow({ doc, canEdit, canDelete, canRequestRemoval, canMarkPaid, currentUserId, onEdit, onDelete, onRequestRemoval, onMarkPayment, accent, outstanding, paid, onAudienceChanged }) {
  // When currentUserId is set (client portal), only allow edit/delete of docs
  // the current user uploaded. Staff (currentUserId=null) see everything.
  const isOwner = !currentUserId || doc.created_by === currentUserId;
  const effCanEdit = canEdit && isOwner;
  const effCanDelete = canDelete && isOwner;
  const effCanRequestRemoval = canRequestRemoval && !isOwner; // owned docs → delete; others → request removal
  const [dlBusy, setDlBusy] = useState(false);
  // Iter 290r — DNU↔CF reassignment. Only visible for quote / sales_order
  // and only when caller has the canEdit flag (managers+ on staff side;
  // never on portal where currentUserId is set).
  const [audBusy, setAudBusy] = useState(false);
  const canReassignAudience = canEdit && !currentUserId
    && (doc.category === "quote" || doc.category === "sales_order");
  const reassignAudience = async (next) => {
    if (audBusy) return;
    if (doc.audience === next) return;
    const wantsDnu = next === "internal";
    if (!window.confirm(
      wantsDnu
        ? "Flag this doc as INTERNAL (DNU)?\n\nIt will be hidden from the customer portal."
        : "Flag this doc as CUSTOMER-FACING (CF)?\n\nThe customer will be able to see it on the portal.",
    )) return;
    setAudBusy(true);
    try {
      await import("../../lib/api").then(({ default: api }) =>
        api.patch(`/sales-documents/${doc.id}/audience`, { audience: next }),
      );
      if (onAudienceChanged) {
        onAudienceChanged(doc.id, next);
      } else {
        // Fallback for call sites that didn't wire a reloader yet —
        // a hard reload guarantees stale state can't leak.
        window.location.reload();
      }
    } catch (e) {
      window.alert("Reassign failed: " + (e?.response?.data?.detail || e?.message || "unknown"));
    } finally {
      setAudBusy(false);
    }
  };
  // [Iter 327] `uploadUrl()` handles bare id, path, and stale full URLs.
  const legacyUrl = doc.file_url ? uploadUrl(doc.file_url) : null;
  const isR2 = !!doc.r2_key;

  const handleDownload = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dlBusy) return;
    setDlBusy(true);
    try {
      if (isR2) await downloadSalesDocToDevice(doc);
      else if (legacyUrl) {
        // Legacy path — just open for download.
        const a = document.createElement("a");
        a.href = legacyUrl;
        a.download = doc.file_name || "document";
        a.target = "_blank";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (err) {
      window.alert("Download failed: " + (err?.message || err));
    } finally {
      setDlBusy(false);
    }
  };

  return (
    <li
      className={`px-3 py-2 hover:bg-white/[0.02] transition-colors ${paid ? "opacity-70" : ""}`}
      data-testid={`account-doc-${doc.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate flex items-center gap-1.5">
            {/* Iter 411 — Title is now a clickable deep-link into the
                unified Sales Document detail view (ExtractedContentPanel
                shows all OCR-hydrated line items, totals, bill-to, etc.).
                Prevents users from having to hunt for the tiny pencil
                icon to see what OCR extracted. */}
            <Link
              to={`/sales-documents/${doc.id}/edit`}
              className="hover:text-[#1FB6C1] focus:text-[#1FB6C1] focus:outline-none transition-colors truncate"
              title="Open full detail — line items, totals, bill-to, chip actions"
              data-testid={`account-doc-open-${doc.id}`}
            >
              {doc.title}
            </Link>
            {/* Wave 29 Chunk 8d — DNU badge on every doc card so HR/reps
                can never confuse an internal engineering doc with a
                customer-facing summary. */}
            {(doc.audience === "internal") && (
              <button
                type="button"
                disabled={!canReassignAudience || audBusy}
                onClick={() => canReassignAudience && reassignAudience("customer")}
                className={`text-[9px] font-mono uppercase tracking-wider border px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 border-rose-400/40 ${canReassignAudience ? "cursor-pointer hover:bg-rose-500/25" : ""} disabled:opacity-50`}
                title={canReassignAudience ? "DNU · click to reassign as Customer-facing" : "Internal / Do Not Use with customer"}
                data-testid={`account-doc-dnu-${doc.id}`}
              >
                {audBusy ? "…" : "DNU"}
              </button>
            )}
            {(doc.audience === "customer" && (doc.category === "quote" || doc.category === "sales_order")) && (
              <button
                type="button"
                disabled={!canReassignAudience || audBusy}
                onClick={() => canReassignAudience && reassignAudience("internal")}
                className={`text-[9px] font-mono uppercase tracking-wider border px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border-emerald-400/30 ${canReassignAudience ? "cursor-pointer hover:bg-emerald-500/20" : ""} disabled:opacity-50`}
                title={canReassignAudience ? "CF · click to reassign as Internal (DNU)" : "Customer-facing summary"}
                data-testid={`account-doc-cf-${doc.id}`}
              >
                {audBusy ? "…" : "CF"}
              </button>
            )}
            {/* 2026-02 — Background OCR status badge. Pending/running → user
                knows the form fields will fill in shortly. Error → user
                can click "Re-scan" inline. */}
            <OcrStatusBadge doc={doc} />
            {doc.status && (
              <span
                className={`text-[9px] font-mono uppercase tracking-wider border px-1.5 py-0.5 rounded ${
                  STATUS_PILL[doc.status] || "bg-white/5 text-[#a0aabf] border-white/10"
                }`}
                data-testid={`account-doc-status-${doc.id}`}
              >
                {doc.status.replace("_", " ")}
              </span>
            )}
            {paid && !doc.status && (
              <span className="text-[9px] font-mono uppercase tracking-wider text-emerald-300 border border-emerald-400/40 px-1 py-0.5 rounded">
                Paid
              </span>
            )}
          </div>
          <div className="text-[10px] text-[#647087] truncate flex items-center gap-1.5 mt-0.5">
            {doc.document_number && (
              <span className="font-mono text-[10px]" style={{ color: accent }}>
                {doc.document_number}
              </span>
            )}
            {doc.document_date && (
              <span>· {new Date(doc.document_date).toLocaleDateString('en-GB')}</span>
            )}
            {doc.amount ? (
              <span className="font-mono text-emerald-300">
                · R {Number(doc.amount).toLocaleString('en-GB')}
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {outstanding && doc.payment_link && (
            <a
              href={doc.payment_link}
              target="_blank"
              rel="noopener noreferrer"
              className="hv-btn hv-btn-primary text-xs px-2 py-1 bg-rose-500 hover:bg-rose-400 border-0"
              title="Pay now"
              data-testid={`account-pay-now-${doc.id}`}
            >
              <CreditCard className="w-3 h-3" /> Pay
            </a>
          )}
          {(isR2 || legacyUrl) && (
            <button
              type="button"
              onClick={handleDownload}
              disabled={dlBusy}
              className="hv-btn hv-btn-ghost text-xs px-2 py-1 disabled:opacity-50"
              title="View / download"
              data-testid={`account-doc-download-${doc.id}`}
            >
              <Download className="w-3 h-3" />
            </button>
          )}
          {canMarkPaid && doc.category === "invoice" && (
            <button
              type="button"
              onClick={onMarkPayment}
              className="hv-btn hv-btn-ghost text-xs px-2 py-1 text-[#1fb6c1]"
              title={doc.payment_status === "outstanding" ? "Mark paid / edit link" : "Mark outstanding / attach pay link"}
              data-testid={`account-mark-payment-${doc.id}`}
            >
              {doc.payment_status === "paid" ? (
                <CheckCircle2 className="w-3 h-3" />
              ) : (
                <CreditCard className="w-3 h-3" />
              )}
            </button>
          )}
          {canEdit && effCanEdit && (
            <button
              type="button"
              onClick={onEdit}
              className="hv-btn hv-btn-ghost text-xs px-2 py-1"
              title="Edit"
              data-testid={`account-doc-edit-${doc.id}`}
            >
              <Pencil className="w-3 h-3" />
            </button>
          )}
          {effCanDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="hv-btn hv-btn-ghost text-xs px-2 py-1 text-rose-300"
              title="Delete"
              data-testid={`account-doc-delete-${doc.id}`}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
          {!effCanDelete && effCanRequestRemoval && (
            <button
              type="button"
              onClick={() => onRequestRemoval?.({ type: "sales_document", id: doc.id, label: doc.title })}
              className="hv-btn hv-btn-ghost text-xs px-2 py-1 text-[#647087]"
              title="Request removal (manager will action)"
              data-testid={`account-doc-request-removal-${doc.id}`}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

/* ---------- Upload modal (R2) ---------- */
export function UploadModal({ category, customerId, canMarkPaid, onClose, onSaved }) {
  const cat = CATEGORIES.find((c) => c.key === category);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [progress, setProgress] = useState(0);
  const [form, setForm] = useState({
    title: "",
    document_number: "",
    document_date: new Date().toISOString().slice(0, 10),
    amount: "",
    notes: "",
    status: "",
    payment_status: category === "invoice" ? "outstanding" : "",
    payment_link: "",
    // Wave 29 Chunk 8d — DNU/CF audience (Dirk 2026-05-11). Force the user
    // to choose for quotes + sales_orders. Invoices/credit_notes/statements
    // are always customer-facing (server enforces this regardless).
    // Iter 425.b — Purchase Orders and payment proofs are customer-side,
    // always customer-facing (no DNU split).
    audience: (category === "invoice" || category === "credit_note" || category === "statement"
      || category === "purchase_order" || category === "payment_proof") ? "customer" : "",
  });
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = (files) => {
    const f = files?.[0];
    if (!f) return;
    setFile(f);
    if (!form.title) {
      setForm((s) => ({ ...s, title: f.name.replace(/\.[^.]+$/, "") }));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      setErr("Pick a file first.");
      return;
    }
    if (!form.title.trim()) {
      setErr("Title is required.");
      return;
    }
    // Enforce DNU/CF choice for engineering docs.
    if ((category === "quote" || category === "sales_order") && !form.audience) {
      setErr("Pick an audience: Internal (DNU) or Customer-facing (CF).");
      return;
    }
    setBusy(true);
    setErr("");
    setProgress(0);
    try {
      // 1. Presign + direct PUT to R2
      const presign = await salesDocPresign({ customerId, file });
      await salesDocPutToR2(presign, file, setProgress);
      // 2. Persist sales_documents record with r2_key
      const body = {
        customer_id: customerId,
        category,
        title: form.title.trim(),
        document_number: form.document_number || null,
        document_date: form.document_date || null,
        amount: form.amount ? Number(form.amount) : null,
        currency: "ZAR",
        file_url: null,
        file_name: file.name,
        file_size: file.size,
        file_type: file.type || null,
        notes: form.notes || "",
        r2_key: presign.key,
        status: form.status || null,
        audience: form.audience || "customer",
      };
      if (category === "invoice" && canMarkPaid) {
        body.payment_status = form.payment_status || null;
        body.payment_link = form.payment_link || null;
      }
      await api.post("/sales-documents", body);
      onSaved?.();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2?.message || "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Upload ${cat?.label?.toLowerCase()?.replace(/s$/, "") || "document"}`}
      testId="sales-doc-upload-modal"
      maxWidth="max-w-lg"
    >
      <form onSubmit={submit} className="space-y-3" data-testid="sales-doc-form">
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFiles(e.dataTransfer.files);
          }}
          className={`relative border-2 border-dashed rounded-md p-5 text-center transition-colors ${
            dragOver
              ? "border-[#1fb6c1] bg-[#1fb6c1]/5"
              : "border-white/10 bg-white/[0.02]"
          }`}
        >
          <input
            type="file"
            onChange={(e) => handleFiles(e.target.files)}
            className="absolute inset-0 opacity-0 cursor-pointer"
            data-testid="sales-doc-file-input"
          />
          <Upload className="w-6 h-6 text-[#647087] mx-auto mb-1" />
          <div className="text-xs text-[#a0aabf]">
            {file ? (
              <span className="text-[#1fb6c1] font-medium">{file.name}</span>
            ) : (
              <>
                <span className="text-[#dde3ec]">Click or drop</span> a file (PDF, image, etc.)
              </>
            )}
          </div>
          {file && (
            <div className="text-[10px] text-[#647087] mt-1">
              {(file.size / 1024).toFixed(0)} KB
            </div>
          )}
        </div>
        {busy && (
          <div className="h-1 bg-white/5 rounded overflow-hidden">
            <div
              className="h-full bg-[#1fb6c1] transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        <Field
          label="Title *"
          value={form.title}
          onChange={(v) => setForm({ ...form, title: v })}
          required
          testid="sales-doc-input-title"
        />
        {(category === "quote" || category === "sales_order") && (
          <div data-testid="sales-doc-audience">
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">
              Audience <span className="text-red-400">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <button
                type="button"
                onClick={() => setForm({ ...form, audience: "internal" })}
                className={`p-3 rounded-md border text-left transition ${form.audience === "internal" ? "border-rose-400/60 bg-rose-500/10" : "border-white/10 hover:border-white/25 bg-white/[0.02]"}`}
                data-testid="sales-doc-audience-internal"
              >
                <div className="text-xs font-semibold text-rose-300">🔴 Internal (DNU)</div>
                <div className="text-[10px] text-[#a0aabf] mt-1">Detailed engineering doc with line items, picking, pricing. Used for project budget + procurement + OCR. <b>Never</b> shared with customer.</div>
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, audience: "customer" })}
                className={`p-3 rounded-md border text-left transition ${form.audience === "customer" ? "border-emerald-400/60 bg-emerald-500/10" : "border-white/10 hover:border-white/25 bg-white/[0.02]"}`}
                data-testid="sales-doc-audience-customer"
              >
                <div className="text-xs font-semibold text-emerald-300">🟢 Customer-facing (CF)</div>
                <div className="text-[10px] text-[#a0aabf] mt-1">Summary doc safe to share via portal. Invoices are issued against these.</div>
              </button>
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <Field
            label="Number / ref"
            value={form.document_number}
            onChange={(v) => setForm({ ...form, document_number: v })}
            mono
            testid="sales-doc-input-number"
          />
          <Field
            label="Date"
            type="date"
            value={form.document_date}
            onChange={(v) => setForm({ ...form, document_date: v })}
            testid="sales-doc-input-date"
          />
        </div>
        <Field
          label="Amount (R)"
          type="number"
          value={form.amount}
          onChange={(v) => setForm({ ...form, amount: v })}
          testid="sales-doc-input-amount"
        />

        {/* Lifecycle status — visible on every category, value list scoped to category */}
        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Status</label>
          <select
            value={form.status || ""}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            className="hv-input mt-1"
            data-testid="sales-doc-input-status"
          >
            <option value="">— Auto —</option>
            {(CATEGORY_STATUSES[category] || []).map((s) => (
              <option key={s} value={s}>
                {s.replace("_", " ")}
              </option>
            ))}
          </select>
        </div>

        {category === "invoice" && canMarkPaid && (
          <PaymentFields
            status={form.payment_status}
            onStatus={(v) => setForm({ ...form, payment_status: v })}
            link={form.payment_link}
            onLink={(v) => setForm({ ...form, payment_link: v })}
          />
        )}

        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Notes</label>
          <BlurTextarea
            value={form.notes}
            onSave={(v) => setForm((s) => ({ ...s, notes: v }))}
            testid="sales-doc-input-notes"
          />
        </div>

        {err && <div className="text-xs text-rose-300">{err}</div>}
        <button
          type="submit"
          disabled={busy}
          className="hv-btn hv-btn-primary w-full"
          data-testid="sales-doc-submit"
        >
          {busy ? `Uploading… ${progress}%` : "Save document"}
        </button>
      </form>
    </Modal>
  );
}

/* ---------- Payment-only modal (staff, quick toggle) ---------- */
export function PaymentModal({ doc, onClose, onSaved }) {
  const [status, setStatus] = useState(doc.payment_status || "outstanding");
  const [link, setLink] = useState(doc.payment_link || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setBusy(true);
    setErr("");
    try {
      await api.patch(`/sales-documents/${doc.id}/payment`, {
        payment_status: status || null,
        payment_link: link || null,
        paid_at: status === "paid" ? new Date().toISOString() : null,
      });
      onSaved?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save payment status.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Payment status · ${doc.title}`}
      testId="sales-doc-payment-modal"
      maxWidth="max-w-md"
    >
      <div className="space-y-3" data-testid="sales-doc-payment-form">
        <PaymentFields
          status={status}
          onStatus={setStatus}
          link={link}
          onLink={setLink}
        />
        {err && <div className="text-xs text-rose-300">{err}</div>}
        <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
          <button type="button" onClick={onClose} className="hv-btn hv-btn-ghost">
            <XCircle className="w-4 h-4" /> Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="hv-btn hv-btn-primary"
            data-testid="sales-doc-payment-save"
          >
            <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function PaymentFields({ status, onStatus, link, onLink }) {
  return (
    <div className="space-y-2 p-2 rounded border border-amber-500/20 bg-amber-500/5" data-testid="sales-doc-payment-fields">
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-300 flex items-center gap-1">
        <CreditCard className="w-3 h-3" /> Payment tracking
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">Status</label>
        <select
          value={status || ""}
          onChange={(e) => onStatus(e.target.value)}
          className="hv-input mt-1"
          data-testid="sales-doc-payment-status"
        >
          <option value="">— No status —</option>
          <option value="outstanding">Outstanding (show Pay-now link)</option>
          <option value="paid">Paid</option>
        </select>
      </div>
      {status === "outstanding" && (
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">
            Payment link <span className="text-[#647087] normal-case">(external Pay-now URL)</span>
          </label>
          <input
            type="url"
            value={link || ""}
            onChange={(e) => onLink(e.target.value)}
            className="hv-input mt-1 font-mono text-xs"
            placeholder="https://pay.payfast.io/..."
            data-testid="sales-doc-payment-link"
          />
          <p className="text-[10px] text-[#647087] mt-1">
            Generate the link in your accounting / payment software, then paste
            it here. Clients see a red "Pay" button next to the invoice in
            their portal.
          </p>
        </div>
      )}
    </div>
  );
}

/* ---------- Edit metadata modal (file is fixed) ---------- */
export function EditModal({ doc, canMarkPaid, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: doc.title || "",
    document_number: doc.document_number || "",
    document_date: doc.document_date || "",
    amount: doc.amount ?? "",
    notes: doc.notes || "",
    category: doc.category,
    status: doc.status || "",
    payment_status: doc.payment_status || "",
    payment_link: doc.payment_link || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...doc,
        ...form,
        amount: form.amount ? Number(form.amount) : null,
      };
      // Only invoices carry payment fields.
      if (payload.category !== "invoice") {
        payload.payment_status = null;
        payload.payment_link = null;
      }
      await api.put(`/sales-documents/${doc.id}`, payload);
      onSaved?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={`Edit document`} testId="sales-doc-edit-modal" maxWidth="max-w-lg">
      <div className="space-y-3" data-testid="sales-doc-edit-form">
        <Field label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Category</label>
          <select
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            className="hv-input mt-1"
          >
            {CATEGORIES.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field
            label="Number / ref"
            value={form.document_number}
            onChange={(v) => setForm({ ...form, document_number: v })}
            mono
          />
          <Field
            label="Date"
            type="date"
            value={form.document_date}
            onChange={(v) => setForm({ ...form, document_date: v })}
          />
        </div>
        <Field
          label="Amount"
          type="number"
          value={form.amount}
          onChange={(v) => setForm({ ...form, amount: v })}
        />
        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Status</label>
          <select
            value={form.status || ""}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            className="hv-input mt-1"
            data-testid="sales-doc-edit-status"
          >
            <option value="">— Auto —</option>
            {(CATEGORY_STATUSES[form.category] || []).map((s) => (
              <option key={s} value={s}>
                {s.replace("_", " ")}
              </option>
            ))}
          </select>
        </div>
        {form.category === "invoice" && canMarkPaid && (
          <PaymentFields
            status={form.payment_status}
            onStatus={(v) => setForm({ ...form, payment_status: v })}
            link={form.payment_link}
            onLink={(v) => setForm({ ...form, payment_link: v })}
          />
        )}
        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Notes</label>
          <BlurTextarea
            value={form.notes}
            onSave={(v) => setForm((s) => ({ ...s, notes: v }))}
          />
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
          <button type="button" onClick={onClose} className="hv-btn hv-btn-ghost">
            <XCircle className="w-4 h-4" /> Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="hv-btn hv-btn-primary"
            data-testid="sales-doc-edit-save"
          >
            <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Field({ label, value, onChange, type = "text", mono, required, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-[#a0aabf]">{label}</label>
      {type === "date" ? (
        <DatePickerInput
          value={value || ""}
          onChange={onChange}
          testId={testid}
        />
      ) : (
        <input
          type={type}
          required={required}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className={`hv-input mt-1 ${mono ? "font-mono" : ""}`}
          data-testid={testid}
        />
      )}
    </div>
  );
}


/* ---------------- OcrStatusBadge --------------------------------------
 * Inline status chip + "Re-scan" trigger for sales docs that went through
 * the 2026-02 background OCR pipeline (POST /sales-documents/bulk-upload
 * or any single upload that schedules background OCR).
 *
 * States:
 *   pending  → "Scanning…" (auto-polls every 4 s for up to 5 minutes)
 *   running  → "Scanning…"
 *   ready    → hidden, UNLESS the OCR detected a mismatch (category /
 *              audience) — in which case a yellow "Looks like a [X]"
 *              banner is shown with Accept / Dismiss buttons. (Dirk
 *              2026-02 — "use the brain and prompt the user".)
 *   error    → red "OCR failed" + inline Re-scan button
 * --------------------------------------------------------------------- */
function OcrStatusBadge({ doc }) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { branding } = useBranding();
  const aiName = branding?.ai_name || "Hidro";
  const initialStatus = doc?.ocr_status || null;
  const [status, setStatus] = useState(initialStatus);
  const [error, setError] = useState(doc?.ocr_error || null);
  const [suggestion, setSuggestion] = useState(doc?.ocr_mismatch_suggestion || null);
  const [reconciliation, setReconciliation] = useState(doc?.ocr_reconciliation || null);
  const [busy, setBusy] = useState(false);
  // Iter 411 — elapsed-time ticker for the "Scanning…" strip. Refreshes
  // every second while OCR is in flight so the user has a visible
  // sense of progress ("Scanning… 45s / 90s / 2m 15s") instead of a
  // silent spinner they eventually give up on.
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    if (status !== "pending" && status !== "running") return undefined;
    const iv = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [status]);

  // [Iter 290zp.13z7] Treat "pending"/"running" as silently complete if
  // the doc was created more than 10 minutes ago. The BG worker either
  // finished and forgot to flip the status, or the row was created by
  // a migration that never scheduled a task. The file is already
  // downloadable in either case — flashing "Scanning…" forever is
  // misleading. The "Re-scan" button on explicit error states is still
  // available for users who want to retry manually.
  const ageMs = (() => {
    const ts = doc?.created_at;
    if (!ts) return Infinity;
    try {
      const t = new Date(ts).getTime();
      return Number.isFinite(t) ? Date.now() - t : Infinity;
    } catch {
      return Infinity;
    }
  })();
  const STALE_AGE_MS = 10 * 60 * 1000;
  const isStaleInFlight =
    (status === "pending" || status === "running") && ageMs > STALE_AGE_MS;

  // Poll while OCR is in flight AND the doc is fresh. Stop on terminal
  // states, on stale in-flight (badge hidden anyway), or after 5 min.
  useEffect(() => {
    if (status !== "pending" && status !== "running") return undefined;
    if (isStaleInFlight) return undefined;
    let cancelled = false;
    const start = Date.now();
    const tick = async () => {
      if (cancelled) return;
      if (Date.now() - start > 5 * 60 * 1000) {
        setStatus("error");
        setError("Timed out waiting for OCR.");
        return;
      }
      try {
        const { data } = await api.get(`/sales-documents/${doc.id}`);
        if (cancelled) return;
        setStatus(data.ocr_status || null);
        setError(data.ocr_error || null);
        setSuggestion(data.ocr_mismatch_suggestion || null);
        setReconciliation(data.ocr_reconciliation || null);
        if (data.ocr_status !== "pending" && data.ocr_status !== "running") return;
      } catch {
        // ignore — next tick will retry
      }
      if (!cancelled) setTimeout(tick, 4000);
    };
    const t = setTimeout(tick, 4000);
    return () => { cancelled = true; clearTimeout(t); };
  }, [status, doc?.id, isStaleInFlight]);

  // Iter 411 — Background reaper transparency. Even when this doc is
  // "ready", the server reaper can silently re-run OCR (e.g. after a
  // pod crash re-hydrates a `running` job). Without a heartbeat poll
  // the UI would show the STALE ocr_completed_at + line count forever
  // — that's exactly the "it fixed itself and I had no idea" bug Dirk
  // caught on SAL2888.11. Poll every 30 s for the first 5 min after
  // load so silent re-runs surface promptly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (status !== "ready") return undefined;
    let cancelled = false;
    let ticks = 0;
    const iv = setInterval(async () => {
      if (cancelled) { clearInterval(iv); return; }
      ticks += 1;
      if (ticks > 10) { clearInterval(iv); return; }  // 10 × 30 s = 5 min
      try {
        const { data } = await api.get(`/sales-documents/${doc.id}`);
        if (cancelled) return;
        if (data.ocr_status !== status) setStatus(data.ocr_status || null);
        setReconciliation(data.ocr_reconciliation || null);
        setError(data.ocr_error || null);
      } catch { /* silent — try next tick */ }
    }, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [status, doc?.id]);

  const rescan = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setBusy(true);
    try {
      await api.post(`/sales-documents/${doc.id}/rescan-ocr`);
      setStatus("pending");
      setError(null);
      setSuggestion(null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Re-scan failed.");
    } finally {
      setBusy(false);
    }
  };

  const applySuggestion = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setBusy(true);
    try {
      await api.post(`/sales-documents/${doc.id}/apply-suggestion`);
      setSuggestion(null);
      // Mutate the parent's doc shallowly so it re-renders with the new
      // category/audience without a full page reload.
      if (doc) {
        if (suggestion?.category) doc.category = suggestion.category;
        if (suggestion?.audience) doc.audience = suggestion.audience;
      }
    } catch {
      /* swallow — keep the banner visible so user can retry */
    } finally {
      setBusy(false);
    }
  };

  const dismissSuggestion = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setBusy(true);
    try {
      await api.post(`/sales-documents/${doc.id}/dismiss-suggestion`);
      setSuggestion(null);
    } catch {
      /* swallow */
    } finally {
      setBusy(false);
    }
  };

  // 1. Mismatch suggestion banner — wins over all other states except error.
  if (status === "ready" && suggestion && (suggestion.category || suggestion.audience)) {
    const tag =
      suggestion.category && suggestion.audience
        ? `${(suggestion.category || "").replace("_", " ")} · ${(suggestion.audience === "internal" ? "DNU" : "CF")}`
        : suggestion.category
        ? (suggestion.category || "").replace("_", " ")
        : suggestion.audience === "internal" ? "DNU (internal)" : "CF (customer-facing)";
    const reason = suggestion.category_reason || suggestion.audience_reason || "";
    return (
      <span
        className="text-[10px] font-mono uppercase tracking-wider border px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-200 border-amber-400/40 inline-flex items-center gap-1 max-w-full"
        title={reason}
        data-testid={`account-doc-ocr-mismatch-${doc.id}`}
      >
        <AlertCircle className="w-2.5 h-2.5 shrink-0" />
        {aiName} thinks this is a{" "}
        <span className="font-semibold">{tag}</span>
        <button
          type="button"
          onClick={applySuggestion}
          disabled={busy}
          className="ml-0.5 text-emerald-300 hover:text-emerald-100 underline disabled:opacity-50"
          data-testid={`account-doc-ocr-apply-${doc.id}`}
        >
          {busy ? "…" : "Apply"}
        </button>
        <button
          type="button"
          onClick={dismissSuggestion}
          disabled={busy}
          className="text-[#647087] hover:text-white underline disabled:opacity-50"
          data-testid={`account-doc-ocr-dismiss-${doc.id}`}
        >
          Dismiss
        </button>
      </span>
    );
  }

  if (!status || status === "none" || isStaleInFlight) return null;

  // Iter 411 — When OCR completes successfully BUT the line-items sum
  // doesn't match the extracted subtotal, raise a loud red banner so
  // the operator never generates a picking slip from silently-incomplete
  // data. This is the P0 safety net Dirk asked for after SAL2888.11
  // silently lost the R 75,000 "3M CONTAINER" line via a fused-row OCR
  // failure. Users always have the Re-scan escape hatch.
  if (status === "ready" && reconciliation?.status === "mismatch") {
    const delta = reconciliation.delta;
    return (
      <div
        className="inline-flex flex-col gap-1 max-w-full"
        data-testid={`account-doc-ocr-recon-mismatch-${doc.id}`}
      >
        <span className="text-[9px] font-mono uppercase tracking-wider border px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-200 border-rose-400/50 inline-flex items-center gap-1 self-start">
          <AlertCircle className="w-2.5 h-2.5 shrink-0" />
          OCR review needed · Δ R {Math.abs(delta || 0).toLocaleString("en-GB", { maximumFractionDigits: 2 })}
          <button
            type="button"
            onClick={rescan}
            disabled={busy}
            className="hover:text-white text-[9px] underline disabled:opacity-50 ml-1"
            data-testid={`account-doc-ocr-recon-rescan-${doc.id}`}
          >
            {busy ? "…" : "Re-scan"}
          </button>
        </span>
        <span className="text-[10px] text-rose-200/80 leading-tight break-words max-w-[320px]">
          {reconciliation.note ||
            `Line items sum (R ${(reconciliation.computed_lines_total || 0).toLocaleString("en-GB")}) does not match extracted subtotal (R ${(reconciliation.extracted_subtotal || 0).toLocaleString("en-GB")}).`}
        </span>
      </div>
    );
  }

  // Iter 411 — Always-visible "Scanned Xm ago · N lines · ✓ reconciled"
  // confirmation on ready docs. Silences the "how do I know the OCR
  // actually ran / finished / succeeded" ambiguity by making the
  // outcome unambiguous at a glance. Also exposes a Re-scan link so
  // the operator can force a retry when they suspect a silent miss.
  if (status === "ready") {
    const completedMs = (() => {
      const ts = doc?.ocr_completed_at || doc?.updated_at;
      if (!ts) return null;
      try { const t = new Date(ts).getTime(); return Number.isFinite(t) ? t : null; } catch { return null; }
    })();
    const agoS = completedMs ? Math.max(0, Math.floor((nowTick - completedMs) / 1000)) : null;
    const agoLabel = agoS == null ? "" :
      agoS < 60 ? `${agoS}s ago` :
      agoS < 3600 ? `${Math.floor(agoS / 60)}m ago` :
      agoS < 86400 ? `${Math.floor(agoS / 3600)}h ago` :
      `${Math.floor(agoS / 86400)}d ago`;
    const lineCount = reconciliation?.line_count ?? (Array.isArray(doc?.line_items) ? doc.line_items.length : null);
    // Iter 411 — Scan-drift indicator. Whenever a re-scan changes the
    // line count, surface the previous number so the operator knows
    // to eyeball the PDF (OCR is non-deterministic and can silently
    // split/merge rows across passes even when totals still reconcile).
    const prevCount = doc?.line_count_previous;
    const drift = doc?.line_count_delta;
    const isReconciled = reconciliation?.status === "match";
    const noSubtotal = reconciliation?.status === "no_subtotal";
    return (
      <span
        className={`text-[9px] font-mono uppercase tracking-wider border px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${
          isReconciled
            ? "bg-emerald-500/10 text-emerald-300 border-emerald-400/30"
            : noSubtotal
            ? "bg-white/5 text-[#a0aabf] border-white/10"
            : "bg-white/5 text-[#a0aabf] border-white/10"
        }`}
        title={
          isReconciled
            ? `Line items sum (R ${(reconciliation.computed_lines_total || 0).toLocaleString("en-GB")}) matches the extracted subtotal — safe to pick from.`
            : noSubtotal
            ? "OCR did not extract a subtotal to reconcile against — manually verify the line items sum before picking."
            : `Scanned ${agoLabel}. ${lineCount ?? "?"} lines extracted.`
        }
        data-testid={`account-doc-ocr-ready-${doc.id}`}
      >
        {isReconciled
          ? <CheckCircle2 className="w-2.5 h-2.5 text-emerald-400" />
          : <AlertCircle className="w-2.5 h-2.5 text-[#a0aabf]" />}
        {isReconciled ? "Reconciled" : noSubtotal ? "No subtotal" : "Scanned"}
        {lineCount != null && <span className="text-[#647087]"> · {lineCount}L</span>}
        {typeof drift === "number" && drift !== 0 && typeof prevCount === "number" && (
          <span
            className={`ml-1 font-mono text-[9px] ${drift > 0 ? "text-emerald-300" : "text-amber-300"}`}
            title={`Previous scan produced ${prevCount} lines. OCR is non-deterministic — visually confirm the row count against the source PDF.`}
          >
            {drift > 0 ? `↑${drift}` : drift} vs {prevCount}L
          </span>
        )}
        {agoLabel && <span className="text-[#647087]"> · {agoLabel}</span>}
        <button
          type="button"
          onClick={rescan}
          disabled={busy}
          className="hover:text-white underline disabled:opacity-50 ml-1"
          data-testid={`account-doc-ocr-refresh-${doc.id}`}
          title="Force a fresh OCR pass"
        >
          {busy ? "…" : "Re-scan"}
        </button>
      </span>
    );
  }

  if (status === "error") {
    // Iter 400.4 — expose the actual error message inline (not just as
    // a hover tooltip which is unreachable on tablet/mobile). Dirk's
    // production OCR failures showed only "OCR failed" with no hint of
    // the cause. Now the reason is visible AND the Re-scan button is
    // prominent so the user can iterate.
    return (
      <div
        className="inline-flex flex-col gap-1 max-w-full"
        data-testid={`account-doc-ocr-error-${doc.id}`}
      >
        <span className="text-[9px] font-mono uppercase tracking-wider border px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 border-rose-400/40 inline-flex items-center gap-1 self-start">
          OCR failed
          <button
            type="button"
            onClick={rescan}
            disabled={busy}
            className="hover:text-white text-[9px] underline disabled:opacity-50"
            data-testid={`account-doc-ocr-rescan-${doc.id}`}
          >
            {busy ? "…" : "Re-scan"}
          </button>
        </span>
        {error && (
          <span
            className="text-[10px] text-rose-300/80 leading-tight break-words max-w-[280px]"
            data-testid={`account-doc-ocr-error-msg-${doc.id}`}
          >
            {error}
          </span>
        )}
      </div>
    );
  }

  // pending / running — Iter 411: show elapsed seconds so the user
  // sees the scan IS running, not stuck. > 90s hints they can force
  // a re-scan without waiting for the 5-minute polling timeout.
  const startedMs = (() => {
    const ts = doc?.ocr_started_at || doc?.created_at;
    if (!ts) return null;
    try { const t = new Date(ts).getTime(); return Number.isFinite(t) ? t : null; } catch { return null; }
  })();
  const elapsedS = startedMs ? Math.max(0, Math.floor((nowTick - startedMs) / 1000)) : null;
  const elapsedLabel = elapsedS == null ? "" :
    elapsedS < 60 ? `${elapsedS}s` :
    `${Math.floor(elapsedS / 60)}m ${elapsedS % 60}s`;
  const stalled = elapsedS != null && elapsedS >= 90;
  return (
    <span
      className={`text-[9px] font-mono uppercase tracking-wider border px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${
        stalled
          ? "bg-amber-500/15 text-amber-200 border-amber-400/50"
          : "bg-[#A78BFA]/15 text-[#A78BFA] border-[#A78BFA]/40"
      }`}
      title={stalled
        ? "OCR is taking longer than usual — click Re-scan to retry."
        : `OCR is running. Started ${elapsedLabel || "just now"}.`}
      data-testid={`account-doc-ocr-pending-${doc.id}`}
    >
      <Loader2 className="w-2.5 h-2.5 animate-spin" /> Scanning{elapsedLabel ? ` · ${elapsedLabel}` : "…"}
      {stalled && (
        <button
          type="button"
          onClick={rescan}
          disabled={busy}
          className="hover:text-white underline disabled:opacity-50 ml-1"
          data-testid={`account-doc-ocr-restart-${doc.id}`}
        >
          {busy ? "…" : "Re-scan"}
        </button>
      )}
    </span>
  );
}
